import React from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Alert from "@components/shared/Alert";
import ENV from "@config/env";
import { useAppContext } from "@context/AppContext";
import { Disclosure } from "@headlessui/react";
import {
  ChevronRightIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/solid";
import classNames from "classnames";
import AccountBalancesWidget from "./AccountBalancesWidget";
import AccountHoldersWidget from "./AccountHoldersWidget";
import DamLevelWidget from "./DamLevelWidget";
import DeclarationChartWidget from "./DeclarationChartWidget";
import RecentNotificationsWidget from "./RecentNotificationsWidget";
import SummaryWidget from "./SummaryWidget";
import WeatherWidget from "./WeatherWidget";
import PageHeading from "@components/zenith/ui/PageHeading";
import { formatSchemePeriod } from "@utils/schemeDay";
import { useDashboardPeriod } from "./useDashboardPeriod";

const Dashboard: React.FunctionComponent = () => {
  const { loggedInInfo, setInfoPanel } = useAppContext();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const showWelcomeMessage = searchParams.get("welcome");
  const { accountingPeriod, periodUnavailable, periodNotice } =
    useDashboardPeriod();

  const greeting = `${t("common.greeting")}, ${
    loggedInInfo?.userDetails?.name.split(" ")[0]
  }`;

  React.useEffect(() => {
    const infos = [
      {
        label: greeting,
        body: t("zenith.dashboard.info.intro"),
        icon: InformationCircleIcon,
      },
      {
        label: t("zenith.dashboard.help"),
        body: t("zenith.dashboard.info.help"),
        icon: QuestionMarkCircleIcon,
      },
    ];

    setInfoPanel(
      <div className="space-y-1 leading-7 text-gray-500 text-sm">
        {infos.map(i => {
          return (
            <Disclosure key={`info-panel-${i.label}`}>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    className={classNames(
                      "group w-full flex justify-between items-center px-3 py-2 rounded",
                      open
                        ? "bg-blue-50 text-primary-1"
                        : " hover:bg-gray-50 hover:text-primary-1",
                    )}
                  >
                    <div className="flex gap-2 items-center">
                      <i.icon
                        className={classNames(
                          "w-5 h-5 shrink-0 text-gray-400",
                          open
                            ? "text-primary-1"
                            : "group-hover:text-primary-1",
                        )}
                      />
                      <span>{i.label}</span>
                    </div>
                    <ChevronRightIcon
                      className={classNames("w-4 h-4 shrink-0", {
                        "rotate-90": open,
                      })}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-4 py-2">
                    {i.body}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          );
        })}
      </div>,
    );
  }, [setInfoPanel, greeting, t]);

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      {showWelcomeMessage ? (
        <Alert type="info" closeButton>
          {t("common.greeting")},{" "}
          <strong>{loggedInInfo?.userDetails?.name}</strong>
          {t("zenith.dashboard.title")}
        </Alert>
      ) : null}

      <PageHeading
        title={greeting}
        sub={
          accountingPeriod
            ? `${t("common.current_accounting_period")}: ${formatSchemePeriod(
                accountingPeriod,
              )}`
            : periodUnavailable
              ? `${t("common.current_accounting_period")}: ${periodNotice}`
              : periodNotice
        }
      />

      <SummaryWidget />

      {/* Side by side once the content, which the info panel narrows, fits both. */}
      <div className="grid grid-cols-fit-120 gap-4 lg:gap-6">
        <AccountBalancesWidget />
        <DeclarationChartWidget />
      </div>

      {/* Seqwater's alone: weather is metric only, and the dams are Seqwater's. */}
      {ENV.CLIENT_ID === "seqwater" ? (
        <div className="grid grid-cols-fit-120 gap-4 lg:gap-6">
          <WeatherWidget />
          <DamLevelWidget />
        </div>
      ) : null}

      <AccountHoldersWidget />
      <RecentNotificationsWidget />
    </div>
  );
};

export default Dashboard;
